import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import ViewSaleModal from './ViewSaleModal';
import EditShippingModal from './EditShippingModal';
import ViewPaymentsModal from './ViewPaymentsModal';
import LedgerModal from './LedgerModal';
import DateFilter from '../DateFilter';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import dateUtils from '../../utils/dateUtils';
import './CommonStyles.css';
import './AllSellStyles.css';
import './Products.css';
import './ProductsActions.css';

const SalesOrderList = ({ searchTerm: externalSearchTerm = '' }) => {
  const navigate = useNavigate();
  const [salesOrders, setSalesOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesError, setSalesError] = useState('');

  // Search & Pagination States
  const [localSearchTerm, setLocalSearchTerm] = useState(externalSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(externalSearchTerm);
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal & Dropdown States
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });
  const [selectedOrderForView, setSelectedOrderForView] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrderForShipping, setSelectedOrderForShipping] = useState(null);
  const [showEditShippingModal, setShowEditShippingModal] = useState(false);
  const [selectedOrderForViewPayment, setSelectedOrderForViewPayment] = useState(null);
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [selectedOrderForLedger, setSelectedOrderForLedger] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);

  // Filter States (Exact same structure as AllSell)
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [shippingStatusFilter, setShippingStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(() => {
    const today = dateUtils.getChicagoToday();
    const last7Days = new Date(today);
    last7Days.setDate(today.getDate() - 6);
    return [last7Days, today];
  });
  const [startDate, endDate] = dateRange;

  // Column Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });

  useEffect(() => {
    setLocalSearchTerm(externalSearchTerm);
  }, [externalSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearchTerm]);

  // Fetch initial dropdown filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [locRes, custRes] = await Promise.all([
          apiService.getBusinessLocations().catch(() => []),
          apiService.getContacts('customer').catch(() => [])
        ]);
        const locs = Array.isArray(locRes) ? locRes : (locRes?.data || []);
        const custs = Array.isArray(custRes) ? custRes : (custRes?.data || []);
        setLocations(locs);
        setCustomers(custs);
      } catch (err) {
        /* fallback */
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch sales orders when filters change
  useEffect(() => {
    loadSalesOrders();
  }, [selectedLocation, selectedCustomer, statusFilter, shippingStatusFilter, dateRange, debouncedSearchTerm]);

  const loadSalesOrders = async () => {
    setLoading(true);
    setSalesError('');
    try {
      const params = {
        per_page: -1,
        search: debouncedSearchTerm,
        location_id: selectedLocation !== 'all' ? selectedLocation : '',
        customer_id: selectedCustomer !== 'all' ? selectedCustomer : '',
        status: statusFilter !== 'all' ? statusFilter : '',
        shipping_status: shippingStatusFilter !== 'all' ? shippingStatusFilter : '',
        start_date: startDate ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}` : '',
        end_date: endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}` : ''
      };

      const response = await apiService.getSalesOrders(params);
      let rawOrders = [];
      if (response && response.success) {
        rawOrders = response.data || [];
      } else if (Array.isArray(response)) {
        rawOrders = response;
      } else if (response && response.data) {
        rawOrders = Array.isArray(response.data) ? response.data : [];
      }
      setSalesOrders(rawOrders);
      setFilteredOrders(rawOrders);
    } catch (error) {
      setSalesError('Failed to load sales orders');
      setSalesOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Sort Handler
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get Sorted Sales Orders
  const getSortedOrders = (ordersToSort) => {
    if (!sortConfig.key) return ordersToSort;

    return [...ordersToSort].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Toggle Action Dropdown with portal positioning matching AllSell exactly
  const toggleDropdown = (orderId, event) => {
    event.stopPropagation();
    if (openDropdownId === orderId) {
      setOpenDropdownId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 160;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 200 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(140, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenDropdownId(orderId);
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openDropdownId) return;
      const clickedBtn = event.target.closest('.actions-btn');
      const clickedMenu = event.target.closest('.action-dropdown-menu');
      if (clickedBtn || clickedMenu) return;
      setOpenDropdownId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Handle status update directly from table status dropdown
  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await apiService.fetchWithAuth(`/connector/api/sales-orders/${orderId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: newStatus })
      });
      if (res && res.success) {
        toast.success('Sales Order status updated');
        loadSalesOrders();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Handle action dropdown options
  const handleViewOrder = (order) => {
    setOpenDropdownId(null);
    setSelectedOrderForView(order);
    setShowViewModal(true);
  };

  const handleEditOrder = (order) => {
    setOpenDropdownId(null);
    navigate('/sell/add', { state: { editSale: order, sale_type: 'sales_order' } });
  };

  const handleDeleteOrder = async (orderId) => {
    setOpenDropdownId(null);
    if (!window.confirm('Are you sure you want to delete this sales order?')) return;
    try {
      await apiService.deleteSale(orderId);
      toast.success('Sales order deleted successfully');
      loadSalesOrders();
    } catch (err) {
      toast.error('Failed to delete sales order');
    }
  };

  const handleEditShipping = (order) => {
    setOpenDropdownId(null);
    setSelectedOrderForShipping(order);
    setShowEditShippingModal(true);
  };

  const handleLedger = (order) => {
    setOpenDropdownId(null);
    setSelectedOrderForLedger(order);
    setShowLedgerModal(true);
  };

  // Export handlers
  const handleExport = (format) => {
    setShowExportDropdown(false);
    const sortedToExport = getSortedOrders(filteredOrders);
    if (!sortedToExport || sortedToExport.length === 0) {
      toast.warning('No sales order data to export');
      return;
    }
    const headers = ['Action', 'Date', 'Order No.', 'Customer Name', 'Contact No.', 'Location', 'Status', 'Shipping Status', 'Quantity Remaining', 'Added By'];
    const rows = sortedToExport.map(o => [
      '',
      o.transaction_date || '',
      o.invoice_no || '',
      o.customer || '',
      o.mobile || o.contact_no || '',
      o.location || '',
      o.status || 'ordered',
      o.shipping_status || '',
      o.so_qty_remaining !== undefined ? o.so_qty_remaining : (o.qty_remaining || '-'),
      o.created_by || o.added_by || 'Admin'
    ]);

    const title = 'Sales Order List';
    const filename = 'sales_orders';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();
  };

  // Pagination & Sorting calculations
  const sortedOrders = getSortedOrders(filteredOrders);
  const totalEntries = sortedOrders.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage) || 1;
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstEntry, indexOfLastEntry);

  const columns = [
    { key: 'transaction_date', label: 'Date', style: { minWidth: '160px', whiteSpace: 'nowrap' } },
    { key: 'invoice_no', label: 'Order No.' },
    { key: 'customer', label: 'Customer Name' },
    { key: 'mobile', label: 'Contact Number' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status' },
    { key: 'shipping_status', label: 'Shipping Status' },
    { key: 'so_qty_remaining', label: 'Quantity Remaining' },
    { key: 'created_by', label: 'Added By' }
  ];

  return (
    <div className="sell-container">
      
      {/* ── Main Content Card (100% Identical to list_draft / AllSell) ── */}
      <div className="main-card">
        
        {/* Table Controls */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select
                value={entriesPerPage}
                onChange={e => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[10, 25, 50, 100].map(n => (
                  <option key={`opt-entries-${n}`} value={n}>{n}</option>
                ))}
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search..."
                value={localSearchTerm}
                onChange={e => setLocalSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(true)}
                title="Open Filters"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>

            {/* Export Dropdown */}
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>

            {/* Add Sales Order Button */}
            <button
              className="control-btn-primary"
              onClick={() => navigate('/sell/add', { state: { sale_type: 'sales_order' } })}
            >
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {/* ── Table Container ── */}
        <div className="products-table-container">
          {loading ? (
            <div className="loading-sales" style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
              <h3>⏳ Loading Sales Order...</h3>
              <p>Fetching Sales Order data from server...</p>
            </div>
          ) : salesError ? (
            <div className="error-sales" style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
              <h3>❌ Error Loading Sales Order</h3>
              <p>{salesError}</p>
              <button onClick={() => loadSalesOrders()} className="retry-btn">
                🔄 Retry
              </button>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="no-sales">
              <h3>📋 No Sales Order Found</h3>
              <p>
                {salesOrders.length === 0
                  ? 'No sales orders have been recorded yet.'
                  : 'No sales order match your current filters.'
                }
              </p>
            </div>
          ) : (
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  {columns.map(({ key, label, style }) => (
                    <th key={key} className="sortable" onClick={() => handleSort(key)} style={{ cursor: 'pointer', ...style }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                        {label}
                        {sortConfig.key === key ? (
                          sortConfig.direction === 'asc' ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 15l-6-6-6 6" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          )
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 15l5 5 5-5" />
                            <path d="M7 9l5-5 5 5" />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentOrders.map(order => (
                  <tr key={order.id}>
                    
                    {/* Action Dropdown Column (100% Identical to Web: View, Edit, Edit Shipping, Shipping Documents, Ledger, Delete) */}
                    <td className="actions-cell">
                      <div className="action-dropdown-container">
                        <button
                          className="actions-btn action-btn-trigger"
                          onClick={e => toggleDropdown(order.id, e)}
                        >
                          Actions <span className="arrow-down">▼</span>
                        </button>

                        {/* Dropdown Portal */}
                        {openDropdownId === order.id && ReactDOM.createPortal(
                          <div
                            className="action-dropdown-menu"
                            style={{
                              position: 'fixed',
                              top: dropdownPosition.top !== null ? `${dropdownPosition.top}px` : 'auto',
                              bottom: dropdownPosition.bottom !== null ? `${dropdownPosition.bottom}px` : 'auto',
                              left: `${dropdownPosition.left}px`,
                              maxHeight: dropdownPosition.maxHeight ? `${dropdownPosition.maxHeight}px` : 'auto',
                              overflowY: 'auto',
                              background: 'white',
                              borderRadius: '8px',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                              border: '1px solid #e2e8f0',
                              zIndex: 99999,
                              minWidth: '170px',
                              padding: '0.35rem 0'
                            }}
                          >
                            <button className="dropdown-item" onClick={() => handleViewOrder(order)}>
                              👁️ View
                            </button>
                            <button className="dropdown-item" onClick={() => handleEditOrder(order)}>
                              ✏️ Edit
                            </button>
                            <button className="dropdown-item" onClick={() => handleEditShipping(order)}>
                              ✏️ Edit Shipping
                            </button>
                            <button className="dropdown-item" onClick={() => { setOpenDropdownId(null); toast.info('No shipping documents uploaded'); }}>
                              📎 Shipping Documents
                            </button>
                            <button className="dropdown-item" onClick={() => handleLedger(order)}>
                              📓 Ledger
                            </button>
                            <button className="dropdown-item delete-item" onClick={() => handleDeleteOrder(order.id)}>
                              🗑️ Delete
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    </td>

                    <td>{order.transaction_date || '-'}</td>
                    <td>{order.invoice_no || '-'}</td>
                    <td>{order.customer || '-'}</td>
                    <td>{order.mobile || order.contact_no || 'N/A'}</td>
                    <td>{order.location || '-'}</td>
                    
                    {/* Status Select Column (Clean inline option styling) */}
                    <td>
                      <select
                        value={order.status || 'ordered'}
                        onChange={e => handleStatusChange(order.id, e.target.value)}
                        style={{
                          padding: '0.3rem 0.6rem',
                          borderRadius: '12px',
                          border: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textTransform: 'capitalize',
                          cursor: 'pointer',
                          outline: 'none',
                          backgroundColor: order.status === 'completed' ? '#dcfce7' : order.status === 'partial' ? '#fef9c3' : order.status === 'revoked' ? '#fee2e2' : '#e0e7ff',
                          color: order.status === 'completed' ? '#166534' : order.status === 'partial' ? '#854d0e' : order.status === 'revoked' ? '#991b1b' : '#3730a3'
                        }}
                      >
                        <option value="ordered" style={{ background: '#ffffff', color: '#1e293b' }}>Ordered</option>
                        <option value="partial" style={{ background: '#ffffff', color: '#1e293b' }}>Partial</option>
                        <option value="completed" style={{ background: '#ffffff', color: '#1e293b' }}>Completed</option>
                        <option value="revoked" style={{ background: '#ffffff', color: '#1e293b' }}>Revoked</option>
                      </select>
                    </td>

                    <td style={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600 }}>
                      {order.shipping_status || 'ORDERED'}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      {order.so_qty_remaining !== undefined ? order.so_qty_remaining : (order.qty_remaining || '-')}
                    </td>

                    <td>
                      {order.created_by || order.added_by || 'Admin'}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination (100% Identical inline-styled structure matching AllSell) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
          <div>
            Showing {filteredOrders.length === 0 ? 0 : indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, totalEntries)} of {totalEntries} entries
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>

      </div>

      {/* ── Filters Modal Popup (Exact Same as AllSell Modal) ── */}
      {showFilters && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '650px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fa fa-filter" style={{ color: '#4f46e5' }} /> Filters
              </h3>
              <button onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>BUSINESS LOCATION:</label>
                <select
                  value={selectedLocation}
                  onChange={e => setSelectedLocation(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                >
                  <option value="all">All</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>CUSTOMER:</label>
                <select
                  value={selectedCustomer}
                  onChange={e => setSelectedCustomer(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                >
                  <option value="all">All</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name || c.supplier_business_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>STATUS:</label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                >
                  <option value="all">All</option>
                  <option value="ordered">Ordered</option>
                  <option value="partial">Partial</option>
                  <option value="completed">Completed</option>
                  <option value="revoked">Revoked</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>SHIPPING STATUS:</label>
                <select
                  value={shippingStatusFilter}
                  onChange={e => setShippingStatusFilter(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                >
                  <option value="all">All</option>
                  <option value="ordered">Ordered</option>
                  <option value="packed">Packed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem' }}>FILTER BY DATE:</label>
                <DateFilter
                  onDateChange={(range) => setDateRange([range.start, range.end])}
                  initialStartDate={startDate}
                  initialEndDate={endDate}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setShowFilters(false)}
                style={{ padding: '0.5rem 2rem', background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Sale Modal */}
      {showViewModal && selectedOrderForView && (
        <ViewSaleModal
          sale={selectedOrderForView}
          onClose={() => { setShowViewModal(false); setSelectedOrderForView(null); }}
        />
      )}

      {/* Edit Shipping Modal */}
      {showEditShippingModal && selectedOrderForShipping && (
        <EditShippingModal
          sale={selectedOrderForShipping}
          onClose={() => { setShowEditShippingModal(false); setSelectedOrderForShipping(null); }}
          onSave={() => loadSalesOrders()}
        />
      )}

      {/* View Payments Modal */}
      {showViewPaymentModal && selectedOrderForViewPayment && (
        <ViewPaymentsModal
          sale={selectedOrderForViewPayment}
          onClose={() => { setShowViewPaymentModal(false); setSelectedOrderForViewPayment(null); }}
        />
      )}

      {/* Ledger Modal */}
      {showLedgerModal && selectedOrderForLedger && (
        <LedgerModal
          sale={selectedOrderForLedger}
          onClose={() => { setShowLedgerModal(false); setSelectedOrderForLedger(null); }}
        />
      )}

    </div>
  );
};

const actionItemStyle = {
  width: '100%',
  padding: '0.5rem 1rem',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  fontSize: '0.85rem',
  color: '#334155',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

export default SalesOrderList;
